import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { formatPrice, formatPriceAmount } from '../../../utils/money';
import type { AdminWalletAdjustmentRequest } from '../../../types/models/Wallet';
import type { ApiResponseAdminWalletAdjust } from '../../../types/api/ApiResponseAdminWallet';
import { BalanceAmount } from '../../Organization/Billing/paymentDisplay';
import { normaliseSignedAmount } from './walletUtils';

interface WalletAdjustModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful POST so the parent refetches the detail. */
  onSaved: () => void;
  clientId: number;
  clientName: string;
  /** Current balance (2-decimal string) — shown with the resulting balance for a sanity check. */
  currentBalance: string;
}

/**
 * Staff correction of a customer's prepaid balance (POST /admin/wallets/{clientId}/adjustments,
 * docs/api-types/admin-wallets.md). The amount is SIGNED: `+` credits the customer, `-` debits. A
 * debit that would overdraw is refused unless `allow_negative` is ticked — that records a receivable
 * (e.g. after a chargeback). Refund procedure (D11): record the negative adjustment FIRST, then refund
 * at Mollie, never the other way round.
 */
const WalletAdjustModal: React.FunctionComponent<WalletAdjustModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  clientId,
  clientName,
  currentBalance,
}) => {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [amountInput, setAmountInput] = useState('');
  const [description, setDescription] = useState('');
  const [allowNegative, setAllowNegative] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ amount?: string; description?: string; allow_negative?: string }>(
    {},
  );

  const normalisedAmount = useMemo(() => normaliseSignedAmount(amountInput), [amountInput]);
  const amountTouched = amountInput.trim() !== '';
  const amountInvalid = amountTouched && normalisedAmount === null;
  const trimmedDescription = description.trim();
  const descriptionInvalid = trimmedDescription.length > 255;
  const isNegative = normalisedAmount !== null && normalisedAmount.startsWith('-');
  const isValid = normalisedAmount !== null && trimmedDescription.length >= 1 && !descriptionInvalid;

  // Display-only arithmetic: the backend recomputes and enforces the claim check.
  const currentNumber = parseFloat(currentBalance);
  const resulting =
    normalisedAmount !== null && !Number.isNaN(currentNumber) ? currentNumber + parseFloat(normalisedAmount) : null;
  const wouldOverdraw = resulting !== null && resulting < 0 && !(currentNumber < 0 && resulting >= currentNumber);

  const reset = () => {
    setAmountInput('');
    setDescription('');
    setAllowNegative(false);
    setFormError(null);
    setFieldErrors({});
  };

  const handleClose = () => {
    if (saving) {
      return;
    }
    reset();
    onClose();
  };

  const submit = async () => {
    if (!isValid || normalisedAmount === null) {
      return;
    }
    setSaving(true);
    setFormError(null);
    setFieldErrors({});
    const payload: AdminWalletAdjustmentRequest = {
      amount: normalisedAmount,
      description: trimmedDescription,
      ...(allowNegative ? { allow_negative: true } : {}),
    };
    try {
      const response = await apiFetch(`/admin/wallets/${clientId}/adjustments`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // JSend: branch on body.status, never response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseAdminWalletAdjust | null;
      if (body?.status === 'success' && body.data?.transaction) {
        addNotification({
          title: 'Adjustment recorded',
          srTitle: 'Success alert',
          description: `${formatPrice(normalisedAmount)} recorded for ${clientName}. New balance: ${
            typeof body.data.balance === 'string' ? formatPrice(body.data.balance) : '—'
          }.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        reset();
        onSaved();
        onClose();
        return;
      }
      const d = body?.data;
      const nextFieldErrors = {
        amount: typeof d?.amount === 'string' ? d.amount : undefined,
        description: typeof d?.description === 'string' ? d.description : undefined,
        allow_negative: typeof d?.allow_negative === 'string' ? d.allow_negative : undefined,
      };
      setFieldErrors(nextFieldErrors);
      const message = body?.message ?? 'Could not record the adjustment. Please try again.';
      setFormError(message);
      addNotification({
        title: 'Could not record the adjustment',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: 'Could not record the adjustment',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const amountHelper =
    fieldErrors.amount ??
    (amountInvalid ? 'Enter a non-zero signed amount with at most 2 decimals, e.g. -25.00 or 40.' : null);
  const descriptionHelper = fieldErrors.description ?? (descriptionInvalid ? 'At most 255 characters.' : null);

  return (
    <Modal variant="small" isOpen={isOpen} onClose={handleClose} aria-labelledby="wallet-adjust-title">
      <ModalHeader
        title="Record an adjustment"
        labelId="wallet-adjust-title"
        description={`Prepaid wallet of ${clientName}`}
      />
      <ModalBody>
        <Form
          id="wallet-adjust-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {formError && <Alert variant="danger" isInline title={formError} />}
          <Alert variant="warning" isInline isPlain title="Not for top-up refunds at Mollie">
            A Mollie refund of a top-up debits the wallet by itself through its webhook, so a negative adjustment for
            the same money debits the customer twice: use Refund on the top-up instead. Likewise, to give back a
            recorded refund that never went through Mollie, use Cancel refund on the top-up — a positive adjustment
            leaves the top-up&apos;s clawback in place, and the healthcheck alert keeps firing. Adjustments are only for
            money moved outside a top-up (a bank transfer, a goodwill correction).
          </Alert>
          <FormGroup label="Amount" isRequired fieldId="wallet-adjust-amount">
            <TextInput
              id="wallet-adjust-amount"
              value={amountInput}
              onChange={(_e, v) => setAmountInput(v)}
              placeholder="-25.00"
              validated={amountHelper ? 'error' : 'default'}
              isDisabled={saving}
              aria-describedby="wallet-adjust-amount-helper"
            />
            <FormHelperText>
              <HelperText id="wallet-adjust-amount-helper">
                <HelperTextItem variant={amountHelper ? 'error' : 'default'}>
                  {amountHelper ?? 'Signed amount in EUR: + credits the customer, − debits. Up to 2 decimals.'}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
          <FormGroup label="Description" isRequired fieldId="wallet-adjust-description">
            <TextInput
              id="wallet-adjust-description"
              value={description}
              onChange={(_e, v) => setDescription(v)}
              placeholder="Remboursement partiel du rechargement #11 (Mollie re_…)"
              validated={descriptionHelper ? 'error' : 'default'}
              isDisabled={saving}
              maxLength={255}
              aria-describedby="wallet-adjust-description-helper"
            />
            <FormHelperText>
              <HelperText id="wallet-adjust-description-helper">
                <HelperTextItem variant={descriptionHelper ? 'error' : 'default'}>
                  {descriptionHelper ?? "Shown on the customer's statement."}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
          <FormGroup fieldId="wallet-adjust-allow-negative">
            <Checkbox
              id="wallet-adjust-allow-negative"
              label="Allow the balance to go negative (record a receivable)"
              isChecked={allowNegative}
              onChange={(_e, checked) => setAllowNegative(checked)}
              isDisabled={saving}
            />
            {fieldErrors.allow_negative && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{fieldErrors.allow_negative}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          {allowNegative && (
            <Alert variant="warning" isInline title="Overdraft allowed">
              Only for recording a receivable, e.g. after a chargeback. Per procedure, record the negative adjustment
              BEFORE refunding at Mollie — never the other way round.
            </Alert>
          )}
          {!allowNegative && wouldOverdraw && (
            <Alert variant="info" isInline title="This debit exceeds the balance">
              The backend will refuse it unless you tick &quot;Allow the balance to go negative&quot;.
            </Alert>
          )}
          <DescriptionList isHorizontal isCompact>
            <DescriptionListGroup>
              <DescriptionListTerm>Current balance</DescriptionListTerm>
              <DescriptionListDescription>
                <BalanceAmount value={currentBalance} />
              </DescriptionListDescription>
            </DescriptionListGroup>
            <DescriptionListGroup>
              <DescriptionListTerm>Resulting balance</DescriptionListTerm>
              <DescriptionListDescription>
                {resulting === null ? (
                  '—'
                ) : (
                  <span
                    style={
                      resulting < 0 ? { color: 'var(--pf-t--global--text--color--status--danger--default)' } : undefined
                    }
                  >
                    {formatPriceAmount(resulting)}
                  </span>
                )}
              </DescriptionListDescription>
            </DescriptionListGroup>
          </DescriptionList>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant={isNegative ? 'danger' : 'primary'}
          type="submit"
          form="wallet-adjust-form"
          isLoading={saving}
          isDisabled={saving || !isValid}
        >
          Record adjustment
        </Button>
        <Button variant="link" onClick={handleClose} isDisabled={saving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WalletAdjustModal;
